import { ApiBaseException } from '@shared/api/errors'
import type { ConfirmationDetailsDto, ConfirmEventBodyDto } from '@shared/core/events'
import type { AxiosError } from 'axios'
import type { ConfirmationErrorKind } from 'src/modules/confirmation/components/confirmation-message/confirmation-message.interface'
import { LATE_CANCEL_ERROR_CODES } from 'src/modules/confirmation/constants/cancellation-policy.constants'
import type { CancellationPolicyStepKind } from 'src/modules/confirmation/types/confirmation.types'

import type { ConfirmationStatus, LoadedConfirmationState } from './confirmation-wrapper.interface'

export function mapSubmitErrorToKind(err: unknown): ConfirmationErrorKind {
  if (err instanceof ApiBaseException) {
    const body = err.body as { error?: string } | null
    if (typeof body?.error === 'string' && body.error === 'event_past') {
      return 'event_past'
    }
    // Before the generic 404: a deleted appointment and a dead url_code share the
    // status, and only this body code tells them apart.
    if (typeof body?.error === 'string' && body.error === 'appointment_not_found') {
      return 'appointment_not_found'
    }
    if (err.status === 404) {
      return 'invalid_link'
    }
  }
  return 'cannot_process'
}

/** Both axios clients reject 4xx as `AxiosError`; `ApiBaseException` is accepted too. */
export function readSubmitErrorCode(err: unknown): string | undefined {
  const code =
    err instanceof ApiBaseException
      ? (err.body as { code?: unknown } | null)?.code
      : (err as AxiosError<{ code?: unknown }> | null)?.response?.data?.code
  return typeof code === 'string' ? code : undefined
}

export function mapSubmitErrorToPolicyStep(err: unknown): CancellationPolicyStepKind | null {
  const code = readSubmitErrorCode(err)
  if (code === LATE_CANCEL_ERROR_CODES.ackRequired) return 'warning'
  if (code === LATE_CANCEL_ERROR_CODES.blocked) return 'blocked'
  return null
}

export function buildCancelBody(urlCode: string, acknowledgeLateCancel: boolean): ConfirmEventBodyDto {
  const body: ConfirmEventBodyDto = { url_code: urlCode, confirm: false }
  if (acknowledgeLateCancel) body.acknowledge_late_cancel = true
  return body
}

/** The late-cancelled check runs before the generic cancelled one, so a reload keeps the done step. */
export function resolveLoadedState(data: ConfirmationDetailsDto): LoadedConfirmationState {
  const eligibility = data.confirmation_eligibility
  if (data.cancellation_policy?.late_cancelled) return 'late_cancelled'
  if (data.confirmed === 'cancelled' || eligibility?.reason === 'already_cancelled') return 'canceled'
  if (eligibility && !eligibility.confirmable && eligibility.reason === 'event_past') return 'event_past'
  return 'open'
}

/** The done step outlives the request status; warning and blocked show only over the open form. */
export function resolveVisibleStep(
  status: ConfirmationStatus,
  step: CancellationPolicyStepKind | null,
): CancellationPolicyStepKind | null {
  if (step === 'late_done') return step
  return status === 'pending' ? step : null
}
